import { Badge, Empty, Flex, Space, Spin, Tag, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';

import type { CertPoGroup } from '../../redux/api/material-certs';
import { BUCKETS } from './buckets';
import SiteTag from './SiteTag';
import VendorTag from './VendorTag';

const { Text } = Typography;

interface Props {
	groups: CertPoGroup[];
	loading: boolean;
	selectedPo: string | null;
	onSelect: (po: string) => void;
}

/**
 * POs with certificate work outstanding, busiest first.
 *
 * Receiving thinks in purchase orders -- one Alro PO is one truck -- so that
 * is the unit of work here rather than the individual heat.
 */
export default function PoList({ groups, loading, selectedPo, onSelect }: Props) {
	if (loading) {
		return (
			<Flex justify="center" style={{ padding: 24 }}>
				<Spin />
			</Flex>
		);
	}
	if (!groups.length) {
		return <Empty description="Nothing waiting. Use Fetch from mailbox to pull in new Alro and McMaster mail." />;
	}

	return (
		<div>
			{groups.map((group) => {
				const isSelected = group.po === selectedPo;
				const done = group.pending_count === 0;
				return (
					<div
						key={group.po}
						role="button"
						tabIndex={0}
						onClick={() => onSelect(group.po)}
						onKeyDown={(event) => {
							if (event.key === 'Enter' || event.key === ' ') onSelect(group.po);
						}}
						style={{
							padding: '8px 12px',
							cursor: 'pointer',
							borderLeft: `3px solid ${isSelected ? '#1677ff' : 'transparent'}`,
							background: isSelected ? '#e6f4ff' : undefined,
							borderBottom: '1px solid #f0f0f0',
						}}
					>
						<Flex justify="space-between" align="center" gap={4}>
							<Space size={4}>
								<Text strong>PO {group.po}</Text>
								<VendorTag vendors={group.vendor_keys} />
								<SiteTag name={group.building_name} compact />
							</Space>
							{done ? (
								<Tag color="default">Done</Tag>
							) : (
								<Badge count={group.pending_count} />
							)}
						</Flex>
						<Flex gap={4} wrap style={{ marginTop: 4 }}>
							{group.ready_count > 0 && (
								<Tag color={BUCKETS.ready.color}>
									{group.ready_count} ready
								</Tag>
							)}
							{group.waiting_cert_count > 0 && (
								<Tag color={BUCKETS.waiting_cert.color}>
									{group.waiting_cert_count} waiting
								</Tag>
							)}
							{group.orphan_cert_count > 0 && (
								<Tag color={BUCKETS.orphan_cert.color}>
									{group.orphan_cert_count} no slip
								</Tag>
							)}
							{group.supporting_document_count > 0 && (
								<Tooltip title={BUCKETS.supporting_document.hint}>
									<Tag color={BUCKETS.supporting_document.color}>
										{group.supporting_document_count} supporting
									</Tag>
								</Tooltip>
							)}
							{group.no_po_count > 0 && (
								<Tooltip title={BUCKETS.no_po.hint}>
									<Tag color={BUCKETS.no_po.color}>
										{group.no_po_count} no PO
									</Tag>
								</Tooltip>
							)}
							{group.mixed_heat && (
								<Tooltip title="At least one packing-slip line carries more than one heat.">
									<Tag>mixed heats</Tag>
								</Tooltip>
							)}
						</Flex>
						{group.oldest_received_at && (
							<Text type="secondary" style={{ fontSize: 12 }}>
								In queue since{' '}
								{dayjs(group.oldest_received_at).format('MMM D, h:mm A')}
							</Text>
						)}
					</div>
				);
			})}
		</div>
	);
}
